import { useMemo } from "react";
import { useGrowStore } from "../../../hooks/useGrowStore";
import { roadmapTracks } from "../../../mocks/roadmap";
import { currentUser } from "../../../mocks/dashboard";

function ScoreBar({ score, color, label }: { score: number; color: string; label: string }) {
  return (
    <div className="flex flex-col items-center gap-2 flex-1">
      <div className="relative w-full h-28 flex items-end justify-center">
        <div className="w-full rounded-t-xl transition-all duration-700" style={{ height: `${score}%`, backgroundColor: color, opacity: 0.85 }} />
      </div>
      <span className="text-xs font-bold text-[#191F28]">{score}</span>
      <span className="text-[10px] text-[#8B95A1] text-center leading-tight whitespace-nowrap overflow-hidden w-full text-ellipsis">{label}</span>
    </div>
  );
}

function RingProgress({ pct, color, size = 60 }: { pct: number; color: string; size?: number }) {
  const r = (size / 2) * 0.76;
  const circumference = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#F2F4F6" strokeWidth="5" />
      <circle
        cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="5"
        strokeDasharray={`${(pct / 100) * circumference} ${circumference}`}
        strokeLinecap="round"
        style={{ transition: "stroke-dasharray 0.8s ease" }}
      />
    </svg>
  );
}

export default function LearningStatsSection() {
  const { store, isClassCompleted } = useGrowStore();

  const avgScore = useMemo(() => {
    if (!store.completedClasses.length) return 0;
    const sum = store.completedClasses.reduce((a, c) => a + c.avgScore, 0);
    return Math.round(sum / store.completedClasses.length);
  }, [store.completedClasses]);

  const bestClass = useMemo(() => {
    if (!store.completedClasses.length) return null;
    return store.completedClasses.reduce((a, b) => (a.avgScore > b.avgScore ? a : b));
  }, [store.completedClasses]);

  const trackProgress = useMemo(() =>
    roadmapTracks.map((t) => {
      const done = t.steps.filter((s) => isClassCompleted(s.classId)).length;
      return { id: t.id, title: t.title, emoji: t.emoji, color: t.color, done, total: t.steps.length, pct: Math.round((done / t.steps.length) * 100) };
    }),
  [isClassCompleted]);

  const metrics = [
    { label: "평균 점수", value: avgScore ? `${avgScore}점` : "-", icon: "ri-bar-chart-2-line", color: "#00D873" },
    { label: "완료 클래스", value: `${store.completedClasses.length}개`, icon: "ri-graduation-cap-line", color: "#FF8C00" },
    { label: "획득 포인트", value: `${store.totalPoints.toLocaleString()}P`, icon: "ri-coin-line", color: "#E91E63" },
    { label: "획득 배지", value: `${store.earnedBadgeIds.length}개`, icon: "ri-medal-line", color: "#7C3AED" },
  ];

  const recentScores = store.completedClasses.slice(0, 6).reverse();

  return (
    <div className="bg-white rounded-3xl p-7">
      {/* Header */}
      <div className="flex items-center justify-between mb-7">
        <div>
          <h3 className="text-xl font-black text-[#191F28]">학습 통계</h3>
          <p className="text-xs text-[#8B95A1] mt-0.5">내 학습 현황을 한눈에 확인해요</p>
        </div>
        {bestClass && (
          <div className="text-right">
            <div className="text-xs text-[#8B95A1]">최고 점수 클래스</div>
            <div className="text-sm font-extrabold text-[#191F28] mt-0.5">{bestClass.classTitle}</div>
            <div className="text-xs text-[#00D873] font-bold">{bestClass.avgScore}점</div>
          </div>
        )}
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        {metrics.map((m) => (
          <div key={m.label} className="bg-[#F2F4F6] rounded-2xl p-4">
            <div className="w-8 h-8 flex items-center justify-center mb-2" style={{ color: m.color }}>
              <i className={`${m.icon} text-xl`} />
            </div>
            <div className="text-xl font-black text-[#191F28]">{m.value}</div>
            <div className="text-xs text-[#8B95A1] mt-0.5">{m.label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* ── Score History Chart ── */}
        <div>
          <h4 className="text-sm font-extrabold text-[#191F28] mb-4">클래스별 점수 히스토리</h4>
          {recentScores.length === 0 ? (
            <div className="h-40 flex flex-col items-center justify-center bg-[#F2F4F6] rounded-2xl gap-3">
              <i className="ri-bar-chart-box-line text-4xl text-[#C8CDD4]" />
              <p className="text-xs text-[#8B95A1]">클래스를 완료하면 점수가 여기에 쌓여요</p>
            </div>
          ) : (
            <div>
              <div className="flex items-end gap-2 h-36">
                {recentScores.map((cls, i) => {
                  const barColor = cls.avgScore >= 90 ? "#00D873" : cls.avgScore >= 80 ? "#FF8C00" : cls.avgScore >= 70 ? "#E91E63" : "#C8CDD4";
                  return (
                    <div key={cls.classId} className="flex-1 flex flex-col items-center gap-1 group cursor-pointer" title={`${cls.classTitle}: ${cls.avgScore}점`}>
                      <div className="text-xs font-bold opacity-0 group-hover:opacity-100 transition-all" style={{ color: barColor }}>{cls.avgScore}</div>
                      <div className="w-full rounded-t-xl transition-all hover:opacity-90" style={{ height: `${(cls.avgScore / 100) * 112}px`, backgroundColor: barColor }} />
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-2 mt-3 justify-end">
                {[{ c: "#00D873", l: "90↑" }, { c: "#FF8C00", l: "80↑" }, { c: "#E91E63", l: "70↑" }].map((item) => (
                  <div key={item.l} className="flex items-center gap-1">
                    <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: item.c }} />
                    <span className="text-[10px] text-[#8B95A1]">{item.l}</span>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 flex-wrap mt-3">
                {recentScores.map((cls, i) => (
                  <span key={cls.classId} className="text-[9px] text-[#8B95A1] bg-[#F2F4F6] px-2 py-0.5 rounded-full">
                    {i + 1}. {cls.classTitle.slice(0, 8)}..
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── Roadmap Track Progress ── */}
        <div>
          <h4 className="text-sm font-extrabold text-[#191F28] mb-4">로드맵 트랙 진행률</h4>
          <div className="space-y-4">
            {trackProgress.map((t) => (
              <div key={t.id} className="flex items-center gap-4">
                <div className="relative w-12 h-12 flex-shrink-0">
                  <RingProgress pct={t.pct} color={t.color} size={48} />
                  <div className="absolute inset-0 flex items-center justify-center text-lg">{t.emoji}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-extrabold text-[#191F28] truncate pr-2">{t.title}</span>
                    <span className="text-xs font-black flex-shrink-0" style={{ color: t.color }}>{t.pct}%</span>
                  </div>
                  <div className="h-1.5 bg-[#F2F4F6] rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-700" style={{ width: `${t.pct}%`, backgroundColor: t.color }} />
                  </div>
                  <div className="text-[10px] text-[#8B95A1] mt-1">{t.done}/{t.total} 완료</div>
                </div>
                {t.pct === 100 && (
                  <div className="flex-shrink-0 w-7 h-7 rounded-full bg-[#00D873]/10 flex items-center justify-center">
                    <i className="ri-trophy-fill text-[#00D873] text-sm" />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Score detail breakdown for last completed class */}
          {store.completedClasses[0] && (
            <div className="mt-5 bg-[#F2F4F6] rounded-2xl p-4">
              <p className="text-xs font-bold text-[#8B95A1] mb-3">최근 클래스 세부 점수</p>
              <p className="text-sm font-extrabold text-[#191F28] mb-3 truncate">{store.completedClasses[0].classTitle}</p>
              {Object.entries(store.completedClasses[0].scores).map(([k, v]) => {
                const labelMap: Record<string, string> = { creativity: "창의성", specificity: "구체성", completeness: "완성도" };
                const colorMap: Record<string, string> = { creativity: "#FF8C00", specificity: "#7C3AED", completeness: "#00D873" };
                return (
                  <div key={k} className="flex items-center gap-3 mb-2">
                    <span className="text-xs text-[#8B95A1] w-14 flex-shrink-0">{labelMap[k]}</span>
                    <div className="flex-1 h-1.5 bg-white rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${v}%`, backgroundColor: colorMap[k] }} />
                    </div>
                    <span className="text-xs font-bold text-[#191F28] w-8 text-right">{v}점</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
